import { Check, CheckCircle2, X } from 'lucide-react';
import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import * as ui from '../components/ui/styles';
import { useModal } from '../hooks/use-modal';

export interface ConfirmOptions {
  title?: string;
  danger?: boolean;
  /** Money that moved, an approval: the button green, a check in a green circle beside the title. */
  success?: boolean;
  /** One line under the title: what it is about ("100.000đ cho bill …"). */
  subtitle?: string;
  /** Label and value rows in a box, before the message (who, how much, when). */
  details?: Array<[string, string]>;
  confirmText?: string;
  /** Turns the dialog into a prompt; resolves with the trimmed text. */
  withInput?: boolean;
  inputLabel?: string;
  inputPlaceholder?: string;
  inputRequired?: boolean;
}

/**
 * Resolves to `true`/`false` for a plain confirm, or to the entered string /
 * `null` when `withInput` is set. The null-vs-false split lets a caller tell
 * "cancelled" apart from "submitted empty".
 */
export type ConfirmFn = (message: string, opts?: ConfirmOptions) => Promise<boolean | string | null>;

interface DialogState extends ConfirmOptions {
  message: string;
  resolve: (value: boolean | string | null) => void;
}

const ConfirmContext = createContext<ConfirmFn>(() => Promise.resolve(false));

/** Promise-based replacement for window.confirm — see .claude/rules/ui-conventions.md. */
export const useConfirm = (): ConfirmFn => useContext(ConfirmContext);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<DialogState | null>(null);
  const [inputValue, setInputValue] = useState('');

  const confirm = useCallback<ConfirmFn>(
    (message, opts = {}) =>
      new Promise((resolve) => {
        setInputValue('');
        setState({ ...opts, message, resolve });
      }),
    [],
  );

  const cancelValue = state?.withInput ? null : false;
  const close = (value: boolean | string | null) => {
    state?.resolve(value);
    setState(null);
  };
  const confirmDisabled = Boolean(state?.withInput && state?.inputRequired && !inputValue.trim());

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {/* Above every modal (z-110) and the media library (z-120): a confirm is
          usually asked FROM a modal, and must not open underneath it. */}
      {state && (
        <ConfirmDialog
          state={state}
          inputValue={inputValue}
          setInputValue={setInputValue}
          confirmDisabled={confirmDisabled}
          onCancel={() => close(cancelValue)}
          onConfirm={() => close(state.withInput ? inputValue.trim() : true)}
        />
      )}
    </ConfirmContext.Provider>
  );
}

/**
 * The dialog itself (APG alert dialog), as drawn (Plan BF): the question as its title at the left,
 * a close button at the right, what it is about under it, the details in a box, then the action's
 * words at the bottom right beside Huỷ. Focus starts on the field when there is one, on Huỷ when
 * the action cannot be undone (one Enter must never delete), otherwise on the action; Esc or a
 * click outside cancels; closing gives focus back.
 */
function ConfirmDialog({ state, inputValue, setInputValue, confirmDisabled, onCancel, onConfirm }: {
  state: DialogState;
  inputValue: string;
  setInputValue: (v: string) => void;
  confirmDisabled: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const ref = useModal<HTMLDivElement>(onCancel);
  const tone = state.danger ? ui.button.danger : state.success ? ui.button.success : ui.button.primary;
  return (
    <div className="fixed inset-0 z-[130] flex items-center justify-center p-4">
      <div className="overlay-in absolute inset-0 bg-slate-900/50" onClick={onCancel} />
      <div
        ref={ref}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-title"
        aria-describedby="confirm-message"
        className="modal-in relative w-full max-w-md rounded-3xl bg-white shadow-2xl focus:outline-none dark:bg-navy-800"
      >
        <div className="flex items-start justify-between gap-4 px-6 pb-2 pt-6">
          <div className="flex min-w-0 items-start gap-3">
            {state.success && (
              <span aria-hidden className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
                <CheckCircle2 size={20} />
              </span>
            )}
            <div className="min-w-0">
              <h2 id="confirm-title" className="text-lg font-bold text-slate-800 [overflow-wrap:anywhere] dark:text-slate-100">
                {state.title || 'Xác nhận'}
              </h2>
              {state.subtitle && <p className="mt-1 text-sm text-slate-600 [overflow-wrap:anywhere] dark:text-slate-300">{state.subtitle}</p>}
            </div>
          </div>
          <button type="button" onClick={onCancel} aria-label="Đóng" className={`${ui.iconButton} -mr-2 -mt-1`}>
            <X size={18} aria-hidden />
          </button>
        </div>
        <div className="flex flex-col gap-3 px-6 pb-5 pt-2">
          {state.details && state.details.length > 0 && (
            <dl className="grid grid-cols-[7.5rem_minmax(0,1fr)] gap-x-4 gap-y-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm dark:border-navy-600 dark:bg-navy-900">
              {state.details.map(([k, v]) => (
                <div key={k} className="contents">
                  <dt className="text-slate-500 dark:text-slate-400">{k}</dt>
                  <dd className="min-w-0 font-semibold text-slate-800 [overflow-wrap:anywhere] dark:text-slate-100">{v}</dd>
                </div>
              ))}
            </dl>
          )}
          <p id="confirm-message" className="text-sm text-slate-600 [overflow-wrap:anywhere] dark:text-slate-300">{state.message}</p>
          {state.withInput && (
            <div>
              {state.inputLabel && (
                <label htmlFor="confirm-input" className={ui.label}>
                  {state.inputLabel} {state.inputRequired && <span className="text-rose-700 dark:text-rose-300">*</span>}
                </label>
              )}
              <textarea
                id="confirm-input"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                rows={3}
                data-autofocus
                aria-label={state.inputLabel ? undefined : state.title || 'Nội dung'}
                placeholder={state.inputPlaceholder || ''}
                className={ui.field}
              />
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 px-6 pb-6">
          <button type="button" onClick={onCancel} data-autofocus={state.danger && !state.withInput ? true : undefined} className={ui.button.secondary}>
            Huỷ
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={confirmDisabled}
            data-autofocus={!state.danger && !state.withInput ? true : undefined}
            className={tone}
          >
            {state.success && <Check size={16} aria-hidden />}
            {state.confirmText || 'Xác nhận'}
          </button>
        </div>
      </div>
    </div>
  );
}
